// The prose of one assistant turn, as text somebody can paste somewhere else.
//
// A turn is an ordered list of parts and only some of them are prose: cards and pills are
// drawn from structured receipts and a `choices` block is a row of buttons. Pasting any of
// those produces JSON or a fragment of one, so only `text` parts travel.
//
// The markdown is kept as written. It is what pastes usefully into the places these
// answers go — a doc, a ticket, Slack — and stripping it would throw away the tables and
// headings that are most of the reason a long answer is worth copying.
import { ChatPart } from './chatParts';

/**
 * Studio's own deep links, flattened to the words they wrap.
 *
 * `[the catch-all bucket](studio:tree_node/2:15)` is a link this app resolves and nothing
 * else does. Pasted anywhere outside Studio it is a dead link wrapped around the only part
 * that still means something, so the wrapper goes and the words stay. An ordinary http
 * link is left alone: that one still works wherever it lands.
 */
export function flattenStudioLinks(markdown: string): string {
  return markdown.replace(/\[([^\]]*)\]\(studio:[^)]*\)/g, '$1');
}

/**
 * One turn's prose, or an empty string when it has none.
 *
 * Parts are joined with a blank line rather than a newline: they are separate blocks the
 * agent wrote at different points in the turn, often with a card between them, and run
 * together they read as one paragraph that changes subject halfway.
 */
export function answerText(parts: ChatPart[] | undefined): string {
  return (parts ?? [])
    .filter((part): part is Extract<ChatPart, { kind: 'text' }> => part.kind === 'text')
    .map((part) => flattenStudioLinks(part.text).trim())
    .filter(Boolean)
    .join('\n\n');
}
